import { useState } from "react";
import { useParams, Link, useSearchParams } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { css } from "styled-system/css";
import { Figure, RankedBars, Columns, Ledger } from "~/components/charts";
import { api, type ApiResponse } from "~/lib/api";
import Markdown from "react-markdown";
import { RefreshCw } from "lucide-react";
import { usePeriod } from "~/lib/period";
import { buildReporterDek, buildReporterHeadline } from "./story";
import { fmtDate, fmtDay, fmtHours, fmtNum, periodName } from "~/lib/format";
import { ed, btn, tbl, Masthead } from "~/components/editorial";

interface ReporterTicket {
  id: string;
  key: string;
  summary: string;
  issueType: string;
  status: string;
  statusCategory: string;
  storyPoints: number | null;
  priority: string | null;
  descriptionLength: number;
  createdAt: string;
  resolvedAt: string | null;
  cycleTime: number | null;
  leadTime: number | null;
  project: {
    key: string;
    name: string;
  };
  quality: number;
  qualityBreakdown: string[]; // Positive hints
  negativeHints: string[]; // Negative hints (missing qualities)
}

interface ReporterDetailResponse {
  data: {
    jiraBaseUrl: string | null;
    reporter: {
      id: string;
      name: string;
      email: string | null;
      avatarUrl: string | null;
      role: string;
    };
    summary: {
      ticketsCreated: number;
      ticketsResolved: number;
      ticketsInProgress: number;
      ticketsBacklog: number;
      totalPoints: number;
      avgQuality: number;
      completionRate: number;
      pointsRate: number;
      descriptionRate: number;
    };
    byType: Array<{ type: string; count: number; percentage: number }>;
    byProject: Array<{ key: string; name: string; count: number }>;
    timeline: Array<{ date: string; count: number }>;
    tickets: ReporterTicket[];
  };
}

interface ReporterInsightsResponse {
  reporter: {
    id: string;
    displayName: string;
    email: string | null;
  };
  insights: {
    summary: string;
    strengths: string[];
    improvements: string[];
    recommendations: string[];
    profileType: string;
    generatedAt: string;
    cached: boolean;
  };
}

function typeLabel(type: string): string {
  return type ? type.charAt(0).toUpperCase() + type.slice(1) : "Other";
}

export function ReporterDetailPage() {
  const { reporterId } = useParams();
  const { range, window } = usePeriod();
  const [searchParams] = useSearchParams();
  const suffix = searchParams.toString() ? `?${searchParams.toString()}` : "";
  const now = periodName(window.label);
  const queryClient = useQueryClient();
  const [isRefreshingInsights, setIsRefreshingInsights] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ["reporter-detail", reporterId, range],
    queryFn: () =>
      api.get<ApiResponse<ReporterDetailResponse["data"]>>(
        `/jira/reporters/${reporterId}?range=${range}`,
      ),
    enabled: !!reporterId,
    staleTime: 60 * 1000,
  });

  const {
    data: insightsData,
    isLoading: insightsLoading,
    error: insightsError,
  } = useQuery({
    queryKey: ["reporter-insights", reporterId, range],
    queryFn: () =>
      api.get<ApiResponse<ReporterInsightsResponse>>(
        `/jira/reporters/${reporterId}/insights?range=${range}`,
      ),
    enabled: !!reporterId,
    staleTime: 5 * 60 * 1000,
  });

  const handleRefreshInsights = async () => {
    setIsRefreshingInsights(true);
    try {
      await api.get<ApiResponse<ReporterInsightsResponse>>(
        `/jira/reporters/${reporterId}/insights?range=${range}&refresh=true`,
      );
      await queryClient.invalidateQueries({
        queryKey: ["reporter-insights", reporterId, range],
      });
    } finally {
      setIsRefreshingInsights(false);
    }
  };

  const d = data?.data;
  const insights = insightsData?.data.insights;

  return (
    <div className={ed.page}>
      <div className={ed.inner}>
        <Masthead label="Ticket Authors" meta={`${window.label} · from JIRA`} />
        <Link to={`/reporters${suffix}`} className={ed.back}>
          ← All reporters
        </Link>

        {isLoading ? (
          <div className={ed.loading}>Loading reporter profile…</div>
        ) : error || !d ? (
          <div className={ed.loading}>Reporter not found.</div>
        ) : (
          <ReporterBody
            d={d}
            now={now}
            periodLabel={window.label}
            insights={insights}
            insightsLoading={insightsLoading || isRefreshingInsights}
            insightsError={!!insightsError}
            onRefresh={handleRefreshInsights}
          />
        )}
      </div>
    </div>
  );
}

function ReporterBody({
  d,
  now,
  periodLabel,
  insights,
  insightsLoading,
  insightsError,
  onRefresh,
}: {
  d: ReporterDetailResponse["data"];
  now: string;
  periodLabel: string;
  insights: ReporterInsightsResponse["insights"] | undefined;
  insightsLoading: boolean;
  insightsError: boolean;
  onRefresh: () => void;
}) {
  const { jiraBaseUrl, reporter, summary, byType, byProject, timeline, tickets } = d;
  const types = [...byType].sort((a, b) => b.count - a.count);
  const projects = [...byProject].sort((a, b) => b.count - a.count);
  const topProject = projects[0] ?? null;
  const weeklyMax = timeline.reduce(
    (best, w) => (w.count > best.count ? w : best),
    { date: "", count: 0 },
  );

  return (
    <>
      <div className={ed.kicker}>Reporter · {now}</div>
      <h1 className={ed.headline}>
        {buildReporterHeadline({
          name: reporter.name,
          periodLabel,
          created: summary.ticketsCreated,
          resolved: summary.ticketsResolved,
          completionRate: summary.completionRate,
          pointsRate: summary.pointsRate,
          descriptionRate: summary.descriptionRate,
          avgQuality: summary.avgQuality,
          topProject,
        })}
      </h1>
      <p className={ed.dek}>
        {buildReporterDek({
          name: reporter.name,
          periodLabel,
          created: summary.ticketsCreated,
          resolved: summary.ticketsResolved,
          completionRate: summary.completionRate,
          pointsRate: summary.pointsRate,
          descriptionRate: summary.descriptionRate,
          avgQuality: summary.avgQuality,
          topProject,
        })}
      </p>
      <div className={ed.bylineSplit}>
        <span>
          <b>{reporter.name}</b>
          {reporter.email && <> · {reporter.email}</>} · tickets created in {now}. From JIRA.
        </span>
      </div>

      <Ledger
        hideMissingPrior
        items={[
          { label: "Created", value: fmtNum(summary.ticketsCreated), note: `${fmtNum(summary.ticketsBacklog)} still in backlog` },
          { label: "Resolved", value: fmtNum(summary.ticketsResolved), note: `${summary.completionRate}% of created` },
          { label: "In progress", value: fmtNum(summary.ticketsInProgress) },
          { label: "Story points", value: fmtNum(summary.totalPoints), note: `${summary.pointsRate}% of tickets pointed` },
          { label: "Described", value: `${summary.descriptionRate}%`, note: "100+ character description" },
          {
            label: "Avg quality",
            value: summary.avgQuality.toFixed(1),
            note: "out of 5",
            valueFlag: summary.ticketsCreated >= 3 && summary.avgQuality < 2 ? "caution" : undefined,
          },
        ]}
      />

      <div className={ed.sect}>I. When and what</div>
      <h2 className={ed.h2}>
        {weeklyMax.count > 0
          ? `The busiest week started ${fmtDay(weeklyMax.date)}, with ${fmtNum(weeklyMax.count)} ${weeklyMax.count === 1 ? "ticket" : "tickets"}`
          : `No tickets filed in ${now}`}
      </h2>
      <div className={ed.twoUp}>
        {timeline.length > 0 ? (
          <Figure
            title="Tickets created per week"
            sub={`${now}, weeks starting Monday`}
            method="Issues this person reported, by creation date, grouped into weeks starting Monday. Weeks with none are left out."
            table={{
              columns: ["Week of", "Tickets"],
              rows: timeline.map((w) => [fmtDay(w.date), w.count]),
            }}
          >
            <Columns
              ariaLabel="Tickets created per week"
              valueLabel="Tickets created"
              formatKey={fmtDay}
              format={(v) => String(Math.round(v))}
              tickFormat={(v) => String(Math.round(v))}
              height={170}
              margin={{ t: 24, l: 52 }}
              steps={2}
              barRatio={0.68}
              maxBarWidth={48}
              points={timeline.map((w) => ({ key: w.date, value: w.count }))}
            />
          </Figure>
        ) : (
          <p className={ed.prose}>No tickets in this period.</p>
        )}
        <div>
          {types.length > 0 && (
            <Figure
              title="By issue type"
              sub="The largest type in color."
              method="Issue type as set in JIRA. Tickets created in the period."
            >
              <RankedBars
                ariaLabel="Tickets by issue type"
                width={420}
                rows={types.map((t, i) => ({
                  id: t.type,
                  label: `${typeLabel(t.type)} ${t.percentage}%`,
                  value: t.count,
                  emphasis: i === 0,
                }))}
              />
            </Figure>
          )}
          {projects.length > 0 && (
            <div style={{ marginTop: 20 }}>
              <Figure
                title="By project"
                sub="Where this person files."
                method="JIRA project of each ticket created in the period."
              >
                <RankedBars
                  ariaLabel="Tickets by project"
                  width={420}
                  rows={projects.slice(0, 10).map((p) => ({
                    id: p.key,
                    label: p.name,
                    value: p.count,
                  }))}
                />
              </Figure>
            </div>
          )}
        </div>
      </div>

      <div className={ed.sect}>II. Profile notes</div>
      <div className={styles.notesHead}>
        <h2 className={ed.h2}>
          {insights?.profileType ? insights.profileType : "Profile notes"}
        </h2>
        <button
          type="button"
          onClick={onRefresh}
          disabled={insightsLoading}
          className={btn.icon}
          title="Write the notes again"
          aria-label="Write the notes again"
        >
          <RefreshCw size={13} className={insightsLoading ? styles.spinning : undefined} />
        </button>
      </div>
      <p className={ed.methodMono} style={{ marginTop: 0, marginBottom: 10 }}>
        Written by a language model from this person's tickets and the numbers above. Check before
        quoting.
      </p>
      {insightsLoading ? (
        <div className={ed.loading} style={{ paddingTop: 12, paddingBottom: 12 }}>
          Writing notes…
        </div>
      ) : insightsError ? (
        <p className={ed.small}>The notes couldn't be loaded.</p>
      ) : insights ? (
        <>
          <div className={ed.prose}>
            <Markdown>{insights.summary || ""}</Markdown>
          </div>
          <div className={styles.notesGrid}>
            {(
              [
                ["Strengths", insights.strengths],
                ["Could improve", insights.improvements],
                ["Recommendations", insights.recommendations],
              ] as const
            )
              .filter(([, list]) => list && list.length > 0)
              .map(([title, list]) => (
                <div key={title}>
                  <h3 className={styles.notesTitle}>{title}</h3>
                  <ul className={ed.bullets}>
                    {list.map((item, i) => (
                      <li key={i}>
                        <Markdown>{item}</Markdown>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
          </div>
          {insights.generatedAt && (
            <p className={ed.methodMono}>
              Written {fmtDate(insights.generatedAt)}
              {insights.cached ? " (cached)" : ""}
            </p>
          )}
        </>
      ) : null}

      <div className={ed.sect}>III. The tickets</div>
      <h2 className={ed.h2}>
        Every ticket {reporter.name.split(" ")[0]} filed in {now}
      </h2>
      <p className={ed.small}>
        <span className="num">{fmtNum(tickets.length)}</span> tickets. Under each summary, what
        earned quality points and, after a minus sign, what was missing.
      </p>
      <div className={tbl.scroll}>
        <table className={tbl.table}>
          <thead>
            <tr>
              <th>Key</th>
              <th>Summary</th>
              <th>Type</th>
              <th>Status</th>
              <th data-num>Points</th>
              <th data-num title="0 to 5">Quality</th>
              <th data-num>Cycle</th>
              <th data-num>Created</th>
            </tr>
          </thead>
          <tbody>
            {tickets.map((t) => (
              <tr key={t.id}>
                <td data-mono style={{ whiteSpace: "nowrap" }}>
                  {jiraBaseUrl ? (
                    <a
                      href={`${jiraBaseUrl}/browse/${t.key}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {t.key}
                    </a>
                  ) : (
                    t.key
                  )}
                </td>
                <td>
                  <span className={styles.summary}>{t.summary}</span>
                  {(t.qualityBreakdown.length > 0 || t.negativeHints?.length > 0) && (
                    <span className={tbl.subBlock}>
                      {t.qualityBreakdown.join(" · ")}
                      {t.qualityBreakdown.length > 0 && t.negativeHints?.length > 0 ? " · " : ""}
                      {t.negativeHints?.map((h) => `− ${h}`).join(" · ")}
                    </span>
                  )}
                </td>
                <td style={{ whiteSpace: "nowrap" }}>{typeLabel(t.issueType)}</td>
                <td style={{ whiteSpace: "nowrap" }}>
                  <span className={t.statusCategory === "done" ? ed.muted : undefined}>
                    {t.status}
                  </span>
                </td>
                <td data-num>
                  <span className={t.storyPoints == null ? ed.muted : undefined}>
                    {t.storyPoints ?? "–"}
                  </span>
                </td>
                <td data-num data-flag={t.quality < 1 || undefined}>
                  {t.quality.toFixed(1)}
                </td>
                <td data-num>{t.cycleTime ? fmtHours(t.cycleTime) : "–"}</td>
                <td data-num>{fmtDate(t.createdAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className={ed.sect}>How these numbers are counted</div>
      <dl className={ed.defs}>
        <div>
          <dt>Quality, 0 to 5</dt>
          <dd>
            Per ticket: description of 100+ characters +1, 250+ +2; story points +1; story or epic
            type +1; acceptance criteria keywords +0.5. Shaded cells score under 1.
          </dd>
        </div>
        <div>
          <dt>Resolved</dt>
          <dd>Tickets created in the period that are now in a done status.</dd>
        </div>
        <div>
          <dt>Cycle</dt>
          <dd>Time from creation to resolution. Done tickets are shown in gray.</dd>
        </div>
        <div>
          <dt>Backlog</dt>
          <dd>Created in the period and not yet started.</dd>
        </div>
      </dl>
    </>
  );
}

const styles = {
  notesHead: css({ display: "flex", alignItems: "center", gap: "10px" }),
  notesGrid: css({
    display: "grid",
    gridTemplateColumns: { base: "1fr", md: "repeat(3, minmax(0, 1fr))" },
    gap: "8px 28px",
    "& p": { m: 0, display: "inline" },
  }),
  notesTitle: css({
    fontFamily: "sans",
    fontSize: "10.5px",
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: "ed.ink2",
    borderTopWidth: "1px",
    borderTopStyle: "solid",
    borderColor: "ed.rule",
    pt: "6px",
    m: 0,
    mb: "6px",
  }),
  summary: css({ display: "block", maxWidth: "460px" }),
  spinning: css({ animation: "spin 1s linear infinite" }),
};
